import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useWorkflowActions } from "@/pages/workflows/workflow-actions-hook.ts";
import { useWorkflowExecutions } from "@/pages/workflows/workflow-execution-hook.tsx";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowExecutionsSearch } from "@/queries/workflows/workflow-execution.query.ts";
import { useWorkflowSummary } from "@/queries/workflows/workflow.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ApiWorkflowExecutionFilter,
  ApiWorkflowExecutionListItem,
} from "@/types/workflows/workflow-execution.types.ts";
import { getApiWorkflowTriggerTypeTitle } from "@/types/workflows/workflow.base-enum.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  MetricTile,
  Page,
  PaginationBlock,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const WorkflowSummaryPage = () => {
  const { id } = useParams<{ id: string }>();
  const workflowId = id ? Number(id) : undefined;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModals();

  const { data: summary, isLoading } = useWorkflowSummary(workflowId);

  useTitle(summary?.title ?? t("workflow:workflows"), t("workflow:summary"));

  const { renderEmployeeName, renderDateAndTime } = useFormatter();
  const { getWorkflowExecutionStatusCell } = useWorkflowExecutions();
  const { getWorkflowTriggerDescription, getWorkflowTriggerTypeIcon } = useWorkflows();
  const { canIEdit } = usePermissions();
  const {
    deleteWorkflow,
    isWorkflowDeletePending,
    editWorkflow,
    toggleWorkflowIsActive,
    isWorkflowActivationPending,
    duplicateWorkflow,
  } = useWorkflowActions({
    onDeleteSuccess: async () => {
      navigate("/workflows/library");
    },
  });

  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();

  const {
    filter: { filter: executionsFilter },
  } = useAppFilter<ApiWorkflowExecutionFilter>({
    filters: [library.page_number],
    source: "Workflow summary executions",
  });

  const { data: executions, isLoading: isExecutionsLoading } = useWorkflowExecutionsSearch({
    filter: { ...executionsFilter, workflow_ids: workflowId ? [workflowId] : undefined },
    enabled: !!workflowId && !isWorkflowDeletePending,
  });

  const columns = useMemo<ColumnDef<ApiWorkflowExecutionListItem>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) => {
          const employee = row.row.original.employee;
          return (
            <ListItem
              to={`/workflows/executions/${row.row.original.id}`}
              title={row.row.original.title}
              forceShowAvatar={true}
              behavior={"card"}
              avatarUrl={employee ? getEmployeeAvatarUrl(employee, "lg") : undefined}
              caption={employee ? renderEmployeeName(employee) : undefined}
            />
          );
        },
        accessorKey: "title",
        meta: { disableRowClick: true },
        ...ColumnSizes.employee,
      },
      {
        header: t("common:status"),
        cell: (row) => getWorkflowExecutionStatusCell(row.row.original),
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("common:date"),
        cell: (row) => <Text>{renderDateAndTime(row.row.original.datetime_created_at)}</Text>,
        accessorKey: "datetime_created_at",
        ...ColumnSizes.status,
      },
    ];
  }, [executions]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.workflows.viewed);
  });

  return (
    <Page>
      <Page.Header
        title={summary?.title ?? ""}
        showBack={true}
        backHandler={() =>
          navigate(
            summary?.folder ? `/workflows/library/${summary.folder.id}` : `/workflows/library`
          )
        }
      >
        {summary && (
          <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
            <Button
              isLoading={isWorkflowActivationPending}
              onClick={() => {
                openModal(ConfirmationModal, {
                  question: summary.is_active
                    ? t("workflow:pause_automation")
                    : t("workflow:activate_automation"),
                  text: summary.is_active
                    ? t("workflow:pause_automation_confirm")
                    : t("workflow:activate_automation_confirm"),
                  acceptTitle: t("common:confirm"),
                  onAccept: async () => {
                    await toggleWorkflowIsActive({
                      workflowId: summary.id,
                      currentWorkflowVersionId: summary.current_version_id,
                      is_active: summary.is_active,
                    });
                  },
                });
              }}
              leftIcon={
                <FontAwesomeIcon
                  icon={`fa-light ${summary?.is_active ? "fa-circle-pause" : "fa-circle-play"}`}
                />
              }
            >
              {summary?.is_active
                ? t("workflow:pause_automation")
                : t("workflow:activate_automation")}
            </Button>
            <Button variant={"secondary"} onClick={() => editWorkflow(summary.id)}>
              {t("common:edit")}
            </Button>
            <DropdownMenu>
              {canIEdit(ApiPermission.workflows_create) && (
                <DropdownMenuItem
                  onClick={() => duplicateWorkflow(summary.id)}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
                  title={t("common:duplicate")}
                />
              )}
              <DropdownMenuItem
                variant={"menuDanger"}
                onClick={() => {
                  openModal(ConfirmationModal, {
                    question: t("common:attention"),
                    text: t("common:delete_confirm"),
                    acceptTitle: t("common:delete"),
                    onAccept: async () => {
                      await deleteWorkflow(summary.id);
                    },
                  });
                }}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                title={`${t("common:delete")}...`}
              />
            </DropdownMenu>
          </Stack>
        )}
      </Page.Header>

      <Page.Content>
        {isLoading && <Skeleton className={"w-full h-32"} />}

        {summary && (
          <Stack gap={"lg"}>
            <Stack>
              <Title header={t("workflow:when")} paddingBottom />
              <ListItem
                behavior="card"
                icon={getWorkflowTriggerTypeIcon(summary.trigger_type)}
                preTitle={getApiWorkflowTriggerTypeTitle(summary.trigger_type)}
                title={getWorkflowTriggerDescription(summary)}
              />
            </Stack>

            <div
              className={
                "grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-(--gap-md) py-(--gap-lg)"
              }
            >
              <MetricTile title={t("common:total")} value={summary.execution_counts.total} />
              <MetricTile
                title={t("common:status_completed")}
                value={summary.execution_counts.completed}
              />
              <MetricTile
                title={t("common:status_failed")}
                value={summary.execution_counts.failed}
              />
              <MetricTile
                title={t("common:status_running")}
                value={summary.execution_counts.running}
              />
              <MetricTile
                title={t("common:status_pending_launch")}
                value={summary.execution_counts.pending}
              />
            </div>

            <Stack>
              <Title
                header={t("workflow:executions")}
                caption={t("workflow:recent_executions_description")}
                paddingBottom
              />
              <Table
                data={executions?.items || []}
                columns={columns}
                showSkeleton={isExecutionsLoading}
                isFetching={isExecutionsLoading}
                onRowClick={(row) => navigate(`/workflows/executions/${row.original.id}`)}
              />
              <PaginationBlock
                metadata={executions?.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            </Stack>
          </Stack>
        )}
      </Page.Content>
    </Page>
  );
};
